import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Divider,
  Grid,
  IconButton,
  Button,
  LinearProgress,
  Tooltip,
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  FiberManualRecord as DotIcon,
  InsertDriveFileOutlined as FileIcon,
  LinkOutlined as LinkIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const STATUS_META: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: '#94A3B8' },
  submitted: { label: 'Submitted', color: '#2450C6' },
  in_review: { label: 'In Review', color: '#7C3AED' },
  approved: { label: 'Approved', color: '#16A34A' },
  rejected: { label: 'Rejected', color: '#DC2626' },
  withdrawn: { label: 'Withdrawn', color: '#6B7280' },
};

const PHASE_STATUS_META: Record<string, { label: string; color: string }> = {
  not_started: { label: 'Not Started', color: '#94A3B8' },
  in_progress: { label: 'In Progress', color: '#2450C6' },
  completed: { label: 'Completed', color: '#16A34A' },
  overdue: { label: 'Overdue', color: '#DC2626' },
};

interface Phase {
  id: number;
  phase_number: number;
  name: string;
  description: string | null;
  planned_start_date: string | null;
  planned_finish_date: string | null;
  actual_start_date: string | null;
  actual_finish_date: string | null;
  working_days_allowed: number | null;
  working_days_elapsed: number;
  status: string;
  phase_type?: 'work' | 'review' | 'approval';
  assigned_approver?: { id: number; name: string } | null;
  approval_status?: string;
  approval_decision?: string | null;
  approval_comments?: string | null;
}

interface Activity {
  id: number;
  action: string;
  description: string;
  performed_by: string;
  created_at: string;
}

interface AuditEvent {
  id: number;
  occurred_at: string;
  actor: string;
  action_type: string;
  field_changes: Record<string, any>;
  metadata: { phase_name?: string; phase_number?: number; phase_action?: string };
  reason: string | null;
  source: string;
  request_id: string | null;
}

interface ConsentDetailData {
  id: number;
  reference: string;
  consent_title: string | null;
  consent_type: { code: string; name: string; legislation: string | null; category: { code: string; name: string } };
  project: { name: string; code: string };
  cgb: { code: string; name: string } | null;
  status: string;
  status_percentage: number;
  criticality: number;
  usrn: string | null;
  osmm_toid: string | null;
  dco_area: string | null;
  location_description: string | null;
  consent_required_date: string | null;
  submission_date: string | null;
  target_approval_date: string | null;
  actual_approval_date: string | null;
  consent_expiry_date: string | null;
  scope_description: string | null;
  consent_requirement_assumption: string | null;
  legislation: string | null;
  deliverables: any[] | null;
  documents: any[] | null;
  drive_links: string | null;
  supporting_evidence: {
    id: number;
    title: string | null;
    reference: string | null;
    source_url: string | null;
    connector_id: string;
    review_rationale: string | null;
    reviewed_by: string | null;
    reviewed_at: string | null;
  }[];
  notes: string | null;
  progress_comments: string | null;
  assigned_to: string | null;
  reviewer: string | null;
  backup_user: { id: number; name: string; availability_status: string } | null;
  backup_group: { id: number; name: string } | null;
  ownership_coverage: { status: 'covered' | 'gap' | 'at_risk'; label: string; message: string };
  permissions?: { review_approve: boolean; review_reject: boolean; review_perform: boolean };
  primary_stakeholder: { code: string; name: string } | null;
  secondary_stakeholders: { code: string; name: string }[];
  phases: Phase[];
  activity_log: Activity[];
  audit_events: AuditEvent[];
  ingestion: {
    connector_id: string;
    external_source_id: string | null;
    source_url: string | null;
    source_record_updated_at: string | null;
    retrieved_at: string | null;
    latest_run_id: number | null;
    history: { source_record_id: number; connector_id: string; first_seen_at: string; last_seen_at: string; versions: { id: number; run_id: number; created_at: string }[] }[];
  } | null;
  custom_fields: {
    conditions?: string[];
    obligations?: string[];
    ingestion_source?: string;
    last_checked?: string;
    data_quality?: string;
  } | null;
  created_at: string;
  updated_at: string;
  lifecycle_template?: { code: string; name: string; template_updated_at: string | null; snapshot_created_at: string | null; message: string };
}

const fmt = (d: string | null) => (d ? format(new Date(d), 'dd MMM yyyy') : '—');

const FactRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value}</Typography>
  </Box>
);

const ConsentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [consent, setConsent] = useState<ConsentDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phaseBusy, setPhaseBusy] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/consents/${id}`);
        if (!res.ok) throw new Error('Consent not found');
        setConsent(await res.json());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load consent');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const reloadConsent = async () => {
    const res = await fetch(`${API_BASE}/consents/${id}`);
    if (!res.ok) throw new Error('Consent not found');
    setConsent(await res.json());
  };

  const submitConsent = async () => {
    setActionError(null);
    try {
      const res = await fetch(`${API_BASE}/consents/${id}/submit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.message || payload.error || 'Unable to submit consent');
      await reloadConsent();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Unable to submit consent');
    }
  };

  const progressPhase = async (phase: Phase, action: 'start' | 'complete') => {
    setPhaseBusy(phase.id);
    setActionError(null);
    try {
      const res = await fetch(`${API_BASE}/consents/${id}/phases/${phase.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.message || payload.error || 'Unable to update lifecycle phase');
      await reloadConsent();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Unable to update lifecycle phase');
    } finally {
      setPhaseBusy(null);
    }
  };

  const decideApproval = async (phase: Phase, decision: 'approved' | 'rejected' | 'changes_requested') => {
    const comment = decision === 'approved' ? '' : window.prompt('Add a reason for this decision') || '';
    if (decision !== 'approved' && !comment.trim()) return;
    setPhaseBusy(phase.id); setActionError(null);
    try {
      const res = await fetch(`${API_BASE}/consents/${id}/phases/${phase.id}/decision`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ decision, comment }) });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.message || payload.error || 'Unable to record approval decision');
      await reloadConsent();
    } catch (err) { setActionError(err instanceof Error ? err.message : 'Unable to record approval decision'); }
    finally { setPhaseBusy(null); }
  };

  if (loading) return <LinearProgress />;

  if (error || !consent) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/consents')}>Back to Consent Register</Button>
        <Typography color="error" sx={{ mt: 2 }}>{error || 'Consent not found'}</Typography>
      </Box>
    );
  }

  const statusMeta = STATUS_META[consent.status] || { label: consent.status, color: '#6B7280' };
  const cf = consent.custom_fields || {};
  const driveLinkList = (consent.drive_links || '')
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/consents')}><BackIcon fontSize="small" /></IconButton>
        <Typography variant="caption" color="text.secondary">Consent Register</Typography>
      </Stack>

      <Alert severity={statusMeta.label === 'Approved' ? 'success' : statusMeta.label === 'Rejected' ? 'warning' : 'info'} sx={{ mb: 3 }}>
        This page brings together the consent decision, what must happen next, and the evidence behind it. Detailed source and audit information is available in the supporting panels below.
      </Alert>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h4" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {consent.reference}
            </Typography>
            <Chip size="small" label={statusMeta.label} sx={{ bgcolor: statusMeta.color, color: '#fff' }} />
            <Chip size="small" variant="outlined" label={consent.consent_type.category.name} />
          </Stack>
          <Typography variant="body1" color="text.secondary">
            {consent.consent_title || consent.consent_type.name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {consent.status === 'draft' && <Button variant="contained" onClick={submitConsent}>Submit consent</Button>}
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/consents/${consent.id}/edit`)}>Edit Consent</Button>
        </Stack>
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      <Grid container spacing={3}>
        {/* Main column */}
        <Grid item xs={12} md={8}>
          {/* Progress notes */}
          {consent.progress_comments && (
            <Paper sx={{ p: 2.5, mb: 3, borderLeft: `4px solid ${statusMeta.color}` }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Current Status</Typography>
              <Typography variant="body2">{consent.progress_comments}</Typography>
            </Paper>
          )}

          {/* Lifecycle timeline */}
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
              <Typography variant="subtitle1">Lifecycle Timeline</Typography>
              {consent.lifecycle_template && <Chip size="small" variant="outlined" label={`Source: ${consent.lifecycle_template.code} template`} />}
            </Stack>
            {consent.lifecycle_template && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 1 }}>{consent.lifecycle_template.message}</Typography>}
            {consent.phases.length > 0 && (() => {
              const active = consent.phases.find((p) => p.status !== 'completed');
              const priorComplete = active ? consent.phases.filter((p) => p.phase_number < active.phase_number).every((p) => p.status === 'completed') : false;
              return (
                <Paper variant="outlined" sx={{ p: 1.75, mb: 1.5, bgcolor: '#F7F9FC' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                    <Box>
                      <Typography variant="overline" color="text.secondary">Current phase</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={700}>{active ? `${active.phase_number}. ${active.name}` : 'Lifecycle complete'}</Typography>
                        {active && <Chip size="small" label={(PHASE_STATUS_META[active.status] || { label: active.status }).label} color={active.status === 'in_progress' ? 'primary' : active.status === 'completed' ? 'success' : 'default'} />}
                      </Stack>
                      {active && <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: active.status === 'in_progress' ? 'success.light' : 'primary.light', bgcolor: '#fff' }}>
                        <Typography variant="overline" color="primary.main" fontWeight={700} sx={{ letterSpacing: 1 }}>Next action</Typography>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                          {active.status === 'in_progress' ? 'Complete' : 'Start'} Phase {active.phase_number}: {active.name.replace(/^Phase (?:1 & 2|3|4|5|6|7):\s*/, '')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2.5 }} sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Owner: {consent.assigned_to || consent.reviewer || 'Unassigned'}</Typography>
                          <Typography variant="body2" color="text.secondary">Due: {fmt(active.planned_finish_date)}</Typography>
                        </Stack>
                      </Box>}
                    </Box>
                    {active && <Stack sx={{ flexShrink: 0, alignItems: { xs: 'stretch', sm: 'flex-end' } }} spacing={1}>
                      {active.status === 'not_started' && <Button size="small" variant="contained" disabled={!priorComplete || phaseBusy === active.id} onClick={() => progressPhase(active, 'start')}>{phaseBusy === active.id ? 'Starting…' : 'Start phase'}</Button>}
                      {active.phase_type === 'approval' && active.status === 'in_progress' && (
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                          {consent.permissions?.review_approve && <Button size="small" variant="contained" color="success" disabled={phaseBusy === active.id} onClick={() => decideApproval(active, 'approved')}>Approve</Button>}
                          {consent.permissions?.review_reject && <Button size="small" variant="outlined" color="error" disabled={phaseBusy === active.id} onClick={() => decideApproval(active, 'rejected')}>Reject</Button>}
                          {consent.permissions?.review_reject && <Button size="small" variant="outlined" disabled={phaseBusy === active.id} onClick={() => decideApproval(active, 'changes_requested')}>Request changes</Button>}
                        </Stack>
                      )}
                      {active.status === 'in_progress' && <Button size="small" variant="contained" color="success" disabled={phaseBusy === active.id} onClick={() => progressPhase(active, 'complete')}>{phaseBusy === active.id ? 'Saving…' : 'Complete phase'}</Button>}
                    </Stack>}
                  </Stack>
                  <LinearProgress variant="determinate" value={consent.status_percentage || 0} sx={{ mt: 1.25, height: 6, borderRadius: 3 }} />
                  <Typography variant="caption" color="text.secondary">{consent.status_percentage || 0}% of lifecycle complete</Typography>
                </Paper>
              );
            })()}
            {consent.phases.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {consent.status === 'draft'
                  ? 'Lifecycle phases are prepared from the consent type and scheduled on submission.'
                  : 'Lifecycle phase data is missing for this legacy consent. Ask a platform administrator to run lifecycle repair.'}
              </Typography>
            ) : (
              <Stack sx={{ position: 'relative', pl: 2 }}>
                <Box sx={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: '2px', bgcolor: '#E2E6EC' }} />
                {consent.phases.map((p) => {
                  const meta = PHASE_STATUS_META[p.status] || { label: p.status, color: '#94A3B8' };
                  return (
                    <Stack key={p.id} direction="row" spacing={2} sx={{ py: 1.5, position: 'relative' }}>
                      <DotIcon sx={{ fontSize: 14, color: meta.color, mt: 0.5, zIndex: 1 }} />
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                          <Chip size="small" label={meta.label} sx={{ bgcolor: meta.color, color: '#fff' }} />
                        </Stack>
                        {p.phase_type === 'approval' && p.approval_status && p.approval_status !== 'not_required' && <Typography variant="caption" color="text.secondary">Approval: {p.approval_status.replace('_', ' ')}{p.assigned_approver ? ` · ${p.assigned_approver.name}` : ''}</Typography>}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 3 }} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Planned: {fmt(p.planned_start_date)} → {fmt(p.planned_finish_date)}
                          </Typography>
                          {p.actual_start_date && (
                            <Typography variant="caption" color="text.secondary">
                              Actual: {fmt(p.actual_start_date)} → {fmt(p.actual_finish_date)}
                            </Typography>
                          )}
                          {p.working_days_allowed != null && (
                            <Typography variant="caption" color="text.secondary">
                              {p.working_days_elapsed}/{p.working_days_allowed} working days
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Paper>

          {/* Conditions & requirements */}
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Conditions &amp; Requirements</Typography>
            <Stack spacing={2}>
              {consent.scope_description && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Scope</Typography>
                  <Typography variant="body2">{consent.scope_description}</Typography>
                </Box>
              )}
              {consent.consent_requirement_assumption && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Requirement Basis</Typography>
                  <Typography variant="body2">{consent.consent_requirement_assumption}</Typography>
                </Box>
              )}
              {cf.conditions && cf.conditions.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Approval Conditions</Typography>
                  <Stack spacing={0.75}>
                    {cf.conditions.map((cond, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#EEF2FF', color: '#2450C6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1 }}>
                          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.6rem' }}>{i + 1}</Typography>
                        </Box>
                        <Typography variant="body2">{cond}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
              {cf.obligations && cf.obligations.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Extracted Obligations</Typography>
                  <Stack spacing={0.75}>
                    {cf.obligations.map((ob, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#D97706', flexShrink: 0, mt: 0.6 }} />
                        <Typography variant="body2">{ob}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
              {!consent.scope_description && (!cf.conditions || cf.conditions.length === 0) && (!cf.obligations || cf.obligations.length === 0) && (
                <Typography variant="body2" color="text.secondary">No conditions recorded for this consent yet.</Typography>
              )}
            </Stack>
          </Paper>

          {/* Evidence & documents */}
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Evidence &amp; Documents</Typography>
            <Stack spacing={1}>
              {driveLinkList.map((link, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <LinkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  {/^https?:\/\//.test(link) ? (
                    <Typography
                      component="a"
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ color: 'primary.main' }}
                    >
                      {link}
                    </Typography>
                  ) : (
                    <Typography variant="body2">{link}</Typography>
                  )}
                </Stack>
              ))}
              {consent.documents && consent.documents.length > 0 && consent.documents.map((doc, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <FileIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">{typeof doc === 'string' ? doc : doc.name || JSON.stringify(doc)}</Typography>
                </Stack>
              ))}
              {consent.supporting_evidence.map((evidence) => (
                <Stack key={evidence.id} direction="row" spacing={1} alignItems="flex-start">
                  <FileIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
                  <Box sx={{ minWidth: 0 }}>
                    {evidence.source_url ? <Typography component="a" href={evidence.source_url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ color: 'primary.main', overflowWrap: 'anywhere' }}>
                      {evidence.title || evidence.reference || 'Supporting evidence'}
                    </Typography> : <Typography variant="body2">{evidence.title || evidence.reference || 'Supporting evidence'}</Typography>}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {evidence.connector_id}{evidence.reviewed_by ? ` · linked by ${evidence.reviewed_by}` : ''}
                    </Typography>
                    {evidence.review_rationale && <Typography variant="caption" color="text.secondary" display="block">{evidence.review_rationale}</Typography>}
                  </Box>
                </Stack>
              ))}
              {driveLinkList.length === 0 && (!consent.documents || consent.documents.length === 0) && consent.supporting_evidence.length === 0 && (
                <Typography variant="body2" color="text.secondary">No evidence or supporting documents linked yet.</Typography>
              )}
            </Stack>
          </Paper>

          {/* Audit history */}
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" gutterBottom>Audit History</Typography>
            {consent.audit_events.length === 0 && consent.activity_log.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No activity recorded yet.</Typography>
            ) : (
              <Stack divider={<Divider />}>
                {(consent.audit_events.length ? consent.audit_events : consent.activity_log.slice().reverse()).map((event: AuditEvent | Activity) => {
                  const audit = 'action_type' in event;
                  const label = audit
                    ? (event.action_type === 'consent_phase_started' ? `Started phase ${event.metadata.phase_number || ''}: ${event.metadata.phase_name || 'lifecycle phase'}`
                      : event.action_type === 'consent_phase_completed' ? `Completed phase ${event.metadata.phase_number || ''}: ${event.metadata.phase_name || 'lifecycle phase'}`
                      : event.action_type === 'consent_updated' ? 'Consent details updated'
                      : event.action_type === 'consent_status_changed' ? 'Consent status changed'
                      : event.action_type.replaceAll('_', ' '))
                    : event.description;
                  const actor = audit ? event.actor : event.performed_by;
                  const timestamp = audit ? event.occurred_at : event.created_at;
                  return <Accordion key={event.id} disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <DotIcon sx={{ fontSize: 10, color: '#94A3B8', mt: 0.75 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">{label}</Typography>
                          <Typography variant="caption" color="text.secondary">{actor || 'System'} · {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}</Typography>
                        </Box>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pl: 3.5, pt: 0, pb: 1.5 }}>
                      {audit ? <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">Event ID {event.id} · Source: {event.source}</Typography>
                        {event.metadata.phase_name && <Typography variant="body2">Phase: {event.metadata.phase_number}. {event.metadata.phase_name}</Typography>}
                        {Object.entries(event.field_changes || {}).map(([field, change]) => <Typography key={field} variant="body2"><strong>{field.replaceAll('_', ' ')}</strong>: {String((change as any)?.old ?? '—')} → {String((change as any)?.new ?? '—')}</Typography>)}
                        {event.reason && <Typography variant="body2">Reason: {event.reason}</Typography>}
                        {event.request_id && <Typography variant="caption" color="text.secondary">Request: {event.request_id}</Typography>}
                      </Stack> : <Typography variant="body2" color="text.secondary">Recorded in the consent activity history.</Typography>}
                    </AccordionDetails>
                  </Accordion>;
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Provenance panel */}
          <Accordion sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Box><Typography variant="subtitle1">Where this information came from</Typography><Typography variant="caption" color="text.secondary">Source, retrieval history and data quality</Typography></Box></AccordionSummary>
            <AccordionDetails>
            <Divider sx={{ mb: 2 }} />
            <FactRow label="Ingestion Method" value={consent.ingestion?.connector_id || cf.ingestion_source || '—'} />
            {consent.ingestion && <>
              <FactRow label="External Source ID" value={consent.ingestion.external_source_id || '—'} />
              <FactRow label="Retrieved" value={consent.ingestion.retrieved_at ? format(new Date(consent.ingestion.retrieved_at), 'dd MMM yyyy HH:mm') : '—'} />
              <FactRow label="Source Updated" value={consent.ingestion.source_record_updated_at ? format(new Date(consent.ingestion.source_record_updated_at), 'dd MMM yyyy') : '—'} />
              <FactRow label="Ingestion Versions" value={consent.ingestion.history.reduce((total, item) => total + item.versions.length, 0)} />
              {consent.ingestion.source_url && <Button size="small" component="a" href={consent.ingestion.source_url} target="_blank" rel="noopener noreferrer">Open authority source</Button>}
              {consent.ingestion.latest_run_id && <Button size="small" onClick={() => navigate(`/data-operations/runs/${consent.ingestion!.latest_run_id}`)}>Open ingestion run</Button>}
            </>}
            <FactRow
              label="Data Quality"
              value={
                <Chip
                  size="small"
                  label={cf.data_quality || 'Medium'}
                  sx={{
                    bgcolor: cf.data_quality === 'High' ? '#DCFCE7' : cf.data_quality === 'Low' ? '#FEE2E2' : '#FEF3C7',
                    color: cf.data_quality === 'High' ? '#15803D' : cf.data_quality === 'Low' ? '#B91C1C' : '#92400E',
                  }}
                />
              }
            />
            <FactRow label="Last Verified" value={cf.last_checked ? format(new Date(cf.last_checked), 'dd MMM yyyy') : '—'} />
            </AccordionDetails>
          </Accordion>

          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Key Facts</Typography>
            <Divider sx={{ mb: 2 }} />
            <FactRow label="Origin Authority" value={consent.cgb ? `${consent.cgb.name} (${consent.cgb.code})` : 'Not assigned'} />
            <FactRow label="Legislation" value={consent.legislation || consent.consent_type.legislation || '—'} />
            <FactRow label="Project" value={`${consent.project.name} (${consent.project.code})`} />
            <FactRow label="Consent Type" value={`${consent.consent_type.code} · ${consent.consent_type.name}`} />
            <FactRow label="Consent Criticality" value={`${consent.criticality || 3} · ${['Administrative', 'Low', 'Medium', 'High', 'Critical'][(consent.criticality || 3) - 1]}`} />
            <FactRow label="Assigned To" value={consent.assigned_to || '—'} />
            <FactRow label="Reviewer" value={consent.reviewer || '—'} />
            <FactRow label="Backup person" value={consent.backup_user ? `${consent.backup_user.name}${consent.backup_user.availability_status === 'unavailable' ? ' (unavailable)' : ''}` : '—'} />
            <FactRow label="Backup group" value={consent.backup_group?.name || '—'} />
            <FactRow label="Coverage status" value={<Chip size="small" label={consent.ownership_coverage?.label || 'Coverage gap'} color={consent.ownership_coverage?.status === 'covered' ? 'success' : consent.ownership_coverage?.status === 'at_risk' ? 'warning' : 'default'} />} />
            <Typography variant="caption" color="text.secondary">{consent.ownership_coverage?.message || 'No backup assigned'}</Typography>
          </Paper>

          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Key Dates</Typography>
            <Divider sx={{ mb: 2 }} />
            <FactRow label="Consent Required By" value={fmt(consent.consent_required_date)} />
            <FactRow label="Submitted" value={fmt(consent.submission_date)} />
            <FactRow label="Target Approval" value={fmt(consent.target_approval_date)} />
            <FactRow label="Actual Approval" value={fmt(consent.actual_approval_date)} />
            <FactRow label="Expiry" value={fmt(consent.consent_expiry_date)} />
          </Paper>

          {(consent.usrn || consent.osmm_toid || consent.dco_area || consent.location_description) && (
            <Paper sx={{ p: 2.5, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Location</Typography>
              <Divider sx={{ mb: 2 }} />
              {consent.usrn && <FactRow label="USRN" value={consent.usrn} />}
              {consent.osmm_toid && <FactRow label="OSMM TOID" value={consent.osmm_toid} />}
              {consent.dco_area && <FactRow label="DCO Area" value={consent.dco_area} />}
              {consent.location_description && <FactRow label="Description" value={consent.location_description} />}
            </Paper>
          )}

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" gutterBottom>Stakeholders</Typography>
            <Divider sx={{ mb: 2 }} />
            <FactRow label="Primary" value={consent.primary_stakeholder?.name || 'Not assigned'} />
            {consent.secondary_stakeholders.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Secondary</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {consent.secondary_stakeholders.map((s) => (
                    <Tooltip key={s.code} title={s.code}>
                      <Chip size="small" label={s.name} sx={{ mb: 0.5 }} />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConsentDetail;
